import { DeviceActivityLog } from "models/DeviceActivityLog";
import { GroupActivityLog } from "models/GroupActivityLog";
import { RuleActivationEventLog } from "models/RuleActivationEventLog";
import { SceneActivityLog } from "models/SceneActivityLog";
import moment from "moment";
import React from "react";
import { forkJoin, Observable, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { activityLogRepository } from "repositories/activity-log-repository";

const MAX_ITEMS = 5;

interface FeedItem {
  key: string;
  createdAt: moment.Moment;
  icon: string;
  name?: string;
  action: string;
  tone?: "success" | "error";
  detail: string;
}

// Lỗi một loại log thì bỏ loại đó, không làm hỏng cả khối.
const safe = <T,>(source: Observable<T[]>) => source.pipe(catchError(() => of([] as T[])));

// Gộp 4 loại log của nhà rồi lấy vài dòng mới nhất. HCL chưa rõ nghĩa mã sự kiện nên chưa đưa vào.
function toFeed(
  devices: DeviceActivityLog[],
  groups: GroupActivityLog[],
  scenes: SceneActivityLog[],
  rules: RuleActivationEventLog[]
): FeedItem[] {
  const items: FeedItem[] = [
    ...devices.map((log) => ({
      key: `device-${log.string ?? log.deviceId}-${log.createdAt}`,
      createdAt: moment(log.createdAt),
      icon: "tio-plug_outlined",
      name: log.device?.name ?? log.deviceName,
      action: log.onlineStatusId === 1 ? "Online" : "Offline",
      tone: (log.onlineStatusId === 1 ? "success" : "error") as FeedItem["tone"],
      detail: ["Thiết bị", log.room?.name].filter(Boolean).join(" · "),
    })),
    ...groups.map((log) => ({
      key: `group-${log.id}`,
      createdAt: moment(log.createdAt),
      icon: "tio-layers_outlined",
      name: log.group?.name ?? log.groupName,
      action: "Đổi trạng thái",
      detail: ["Nhóm", log.endUser?.displayName].filter(Boolean).join(" · "),
    })),
    ...scenes.map((log) => ({
      key: `scene-${log.id}`,
      createdAt: moment(log.createdAt),
      icon: "tio-movie",
      name: log.scene?.name ?? log.sceneName,
      action: "Chạy cảnh",
      detail: ["Cảnh", log.room?.name].filter(Boolean).join(" · "),
    })),
    ...rules.map((log) => ({
      key: `rule-${log.id}`,
      createdAt: moment(log.createdAt),
      icon: "tio-autorenew",
      name: log.ruleName,
      action: "Kích hoạt",
      detail: "Kịch bản tự động",
    })),
  ];
  return items
    .sort((a, b) => b.createdAt.valueOf() - a.createdAt.valueOf())
    .slice(0, MAX_ITEMS);
}

interface DormitoryRecentActivityProps {
  dormitoryId?: string;
  onViewAll: () => void;
}

export default function DormitoryRecentActivity(props: DormitoryRecentActivityProps) {
  const { dormitoryId, onViewAll } = props;
  const [feed, setFeed] = React.useState<FeedItem[]>();

  React.useEffect(() => {
    if (!dormitoryId) return;
    const filter: any = { skip: 0, take: MAX_ITEMS, dormitoryId: { equal: dormitoryId } };
    const subscription = forkJoin([
      safe(activityLogRepository.listDeviceLog(filter)),
      safe(activityLogRepository.listGroupLog(filter)),
      safe(activityLogRepository.listSceneLog(filter)),
      safe(activityLogRepository.listRuleLog(filter)),
    ]).subscribe(([devices, groups, scenes, rules]) =>
      setFeed(toFeed(devices ?? [], groups ?? [], scenes ?? [], rules ?? []))
    );
    return () => subscription.unsubscribe();
  }, [dormitoryId]);

  return (
    <section className="dorm-detail__card">
      <div className="dorm-detail__card-head">
        <h2 className="dorm-detail__card-title">Hoạt động gần đây</h2>
        <button type="button" className="dorm-detail__more" onClick={onViewAll}>
          Xem tất cả
          <i className="tio-chevron_right" />
        </button>
      </div>
      {feed === undefined ? (
        <p className="dorm-detail__empty">Đang tải…</p>
      ) : feed.length === 0 ? (
        <p className="dorm-detail__empty">Chưa có hoạt động nào</p>
      ) : (
        <ul className="dorm-detail__feed">
          {feed.map((item) => (
            <li key={item.key}>
              <span className="dorm-detail__feed-time tabular-nums" title={item.createdAt.format("DD/MM/YYYY HH:mm")}>
                {item.createdAt.isSame(moment(), "day")
                  ? item.createdAt.format("HH:mm")
                  : item.createdAt.format("DD/MM")}
              </span>
              <span className="dorm-detail__obj-icon">
                <i className={item.icon} />
              </span>
              <div className="dorm-detail__feed-text">
                <div>
                  <span className="dorm-detail__strong">{item.name || "—"}</span>
                  {" · "}
                  {item.tone ? (
                    <span className={`status-badge status-badge--${item.tone}`}>{item.action}</span>
                  ) : (
                    item.action
                  )}
                </div>
                <div className="dorm-detail__sub">{item.detail}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
